import React, { FunctionComponent, useState, useEffect } from 'react';
import cn from 'classnames';
import { BASE_LABEL, HINT, FLOATING, FLOATING_DISABLED } from './classnames';
import transition from '../styles/transition';
import parseClassnames from '../helpers/parseClassnames';
import { LabelProps } from './types';
import { ColorType } from '../styles/types';
import Text from '../Text';

const Label: FunctionComponent<LabelProps> = ({
  label,
  floating,
  focused,
  disabled,
  chat,
  noPadding,
  className = '',
  style,
  sm,
  darkMode,
  type = 'body3',
  responsive = 'sm',
  ...other
}) => {
  // Useful to set overflow-hidden className after the css transition ended
  const [focusedDelayed, setFocusedDelayed] = useState<boolean>(!!focused);

  useEffect(() => {
    if (focused) {
      setTimeout(() => {
        setFocusedDelayed(true);
      }, 150);
    } else {
      setFocusedDelayed(false);
    }
  }, [focused]);

  let textColor: ColorType =
    focused || (!focused && disabled) ? 'base-lighter' : 'gray-dark';

  if (darkMode) {
    textColor = disabled ? 'gray-dark' : 'base-lighter';
  }

  if (floating) {
    return (
      <Text
        type={focused ? 'caption' : type}
        responsive={responsive}
        color={textColor}
        className={parseClassnames(
          className,
          cn(BASE_LABEL, {
            [`${HINT} ${chat ? 'h-32' : 'h-40'}`]: !focused,
            'h-24': focused,
            'overflow-hidden': focusedDelayed && type === 'body3',
            [FLOATING]: focused && !disabled,
            [FLOATING_DISABLED]: focused && disabled,
            'px-16': !noPadding,
            [focused || chat ? 'pt-8' : 'pt-16']: !noPadding,
          }),
        )}
        style={{
          transition: transition([
            'padding',
            'opacity',
            'color',
            'font-size',
            'line-height',
            'font-weight',
            'overflow',
          ]),
          ...style,
        }}
        {...other}
      >
        {label}
      </Text>
    );
  }

  if (!darkMode) {
    textColor = disabled ? 'base-lighter' : 'gray-dark';
  }

  return (
    <Text
      type={type}
      responsive={responsive}
      color={textColor}
      className={parseClassnames(
        className,
        cn(BASE_LABEL, HINT, {
          hidden: focused,
          'h-32 items-center': chat,
          [chat ? 'pt-8' : 'pt-16']: !noPadding,
          'pl-16': !noPadding,
        }),
      )}
      style={sm ? { paddingTop: 6 } : {}}
      {...other}
    >
      {label}
    </Text>
  );
};

export default Label;
